/**
 * Client API — wrapper axios avec auth automatique + refresh JWT.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Inject access token if present
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const token = sessionStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Refresh token on 401
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refresh = sessionStorage.getItem("refresh_token");
      if (refresh) {
        try {
          const res = await axios.post(`${API}/auth/refresh`, {
            refresh_token: refresh,
          });
          sessionStorage.setItem("access_token", res.data.access_token);
          original.headers.Authorization = `Bearer ${res.data.access_token}`;
          return api(original);
        } catch {
          sessionStorage.clear();
          if (typeof window !== "undefined") {
            window.location.href = "/auth";
          }
        }
      } else {
        sessionStorage.clear();
        if (typeof window !== "undefined") {
          window.location.href = "/auth";
        }
      }
    }
    return Promise.reject(error);
  }
);

// ── Endpoints typés ────────────────────────────────────────────
export const authApi = {
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data),
  refresh: (refresh_token) =>
    api.post("/auth/refresh", { refresh_token }).then((r) => r.data),
};

export const parcoursApi = {
  list: () => api.get("/parcours").then((r) => r.data),
  get: (id) => api.get(`/parcours/${id}`).then((r) => r.data),
  create: (data) => api.post("/parcours", data).then((r) => r.data),
  update: (id, data) => api.patch(`/parcours/${id}`, data).then((r) => r.data),
  toggle: (id) => api.patch(`/parcours/${id}/toggle`).then((r) => r.data),
  remove: (id) => api.delete(`/parcours/${id}`).then((r) => r.data),
};

export const sessionsApi = {
  list: () => api.get("/sessions").then((r) => r.data),
  active: () => api.get("/sessions/active").then((r) => r.data),
};

export const reservationsApi = {
  list: () => api.get("/reservations").then((r) => r.data),
  stats: () => api.get("/reservations/stats").then((r) => r.data),
  get: (id) => api.get(`/reservations/${id}`).then((r) => r.data),
  confirmer: (id) =>
    api.patch(`/reservations/${id}/confirmer`).then((r) => r.data),
  activer: (id) =>
    api.patch(`/reservations/${id}/activer-jeu`).then((r) => r.data),
  annuler: (id) => api.delete(`/reservations/${id}`).then((r) => r.data),
};

export const partenairesApi = {
  list: () => api.get("/partenaires").then((r) => r.data),
  create: (data) => api.post("/partenaires", data).then((r) => r.data),
  update: (id, data) => api.patch(`/partenaires/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/partenaires/${id}`).then((r) => r.data),
};

export const produitsApi = {
  list: (params = {}) => api.get("/produits", { params }).then((r) => r.data),
  catalogueIA: () => api.get("/produits/catalogue-pour-ia").then((r) => r.data),
  create: (data) => api.post("/produits", data).then((r) => r.data),
  update: (id, data) => api.patch(`/produits/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/produits/${id}`).then((r) => r.data),
};

export const communauteApi = {
  list: (params = {}) => api.get("/communaute", { params }).then((r) => r.data),
  stats: () => api.get("/communaute/stats").then((r) => r.data),
  moderer: (id, data) =>
    api.patch(`/communaute/${id}/moderer`, data).then((r) => r.data),
  remove: (id) => api.delete(`/communaute/${id}`).then((r) => r.data),
};

export const classementsApi = {
  dashboard: () => api.get("/classements/dashboard/stats").then((r) => r.data),
  parcours: (id, periode = "semaine") =>
    api.get(`/classements/${id}`, { params: { periode } }).then((r) => r.data),
};

export const financesApi = {
  overview: () => api.get("/finances/overview").then((r) => r.data),
  revenuParJour: (days = 30) =>
    api.get("/finances/revenu-par-jour", { params: { days } }).then((r) => r.data),
  parParcours: () => api.get("/finances/par-parcours").then((r) => r.data),
  topClients: (limit = 5) =>
    api.get("/finances/top-clients", { params: { limit } }).then((r) => r.data),
  exportXlsx: (days = 30) =>
    api.get("/finances/export-xlsx", {
      params: { days },
      responseType: "blob",
    }),
};

export const tenantsApi = {
  me: () => api.get("/tenants/me").then((r) => r.data),
};

export const iaBuilderApi = {
  etat: (parcoursId) =>
    api.get(`/ia-builder/etat/${parcoursId}`).then((r) => r.data),
  saveComplete: (data) =>
    api.post("/ia-builder/save-complete", data).then((r) => r.data),
};

// ─── Public (no auth) ─────────────────────────────────────
export const publicApi = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

export const publicEndpoints = {
  parcours: () => publicApi.get("/public/parcours").then((r) => r.data),
  parcoursDetail: (id) =>
    publicApi.get(`/public/parcours/${id}`).then((r) => r.data),
  disponibilite: (id, date) =>
    publicApi.get(`/public/parcours/${id}/disponibilite`, { params: { date } })
      .then((r) => r.data),
  reserver: (data) =>
    publicApi.post("/public/reserver", data).then((r) => r.data),
  checkout: (data) =>
    publicApi.post("/payments/checkout", data).then((r) => r.data),
  paymentStatus: (sessionId) =>
    publicApi.get(`/payments/status/${sessionId}`).then((r) => r.data),
  galerie: (params = {}) =>
    publicApi.get("/public/galerie", { params }).then((r) => r.data),
  challengeMois: () =>
    publicApi.get("/public/challenge-du-mois").then((r) => r.data),
  voter: (photoId) =>
    publicApi.post(`/public/galerie/${photoId}/vote`).then((r) => r.data),
  partenaires: () => publicApi.get("/public/partenaires").then((r) => r.data),
  producteurs: () => publicApi.get("/public/producteurs").then((r) => r.data),
  trackProducteurClic: (id) =>
    publicApi.post(`/public/partenaires/${id}/clicked`).then((r) => r.data),
};

// ─── Player game API (public, via codeAcces) ─────────────────────
export const gameApi = {
  byCode: (code) =>
    publicApi.get(`/sessions/by-code/${code}`).then((r) => r.data),
  demarrer: (code) =>
    publicApi.patch(`/sessions/by-code/${code}/demarrer`).then((r) => r.data),
  ouvrirCadenas: (sessionId, payload) =>
    publicApi
      .post(`/sessions/${sessionId}/cadenas`, payload)
      .then((r) => r.data),
  indiceUtilise: (code, payload) =>
    publicApi.post(`/sessions/by-code/${code}/indice`, payload).then((r) => r.data),
  // Le Sage du Causse
  sageNouvelle: (code) =>
    publicApi.post(`/sessions/by-code/${code}/sage/nouvelle`).then((r) => r.data),
  sageRepondre: (code, answer) =>
    publicApi
      .post(`/sessions/by-code/${code}/sage/repondre`, { answer })
      .then((r) => r.data),
  sageAbandonner: (code) =>
    publicApi.post(`/sessions/by-code/${code}/sage/abandonner`).then((r) => r.data),
  // Sac Picnic — tenter un code sur un cadenas
  tryCode: (code, codeSaisi) =>
    publicApi
      .post(`/sessions/by-code/${code}/try-code`, { code: codeSaisi })
      .then((r) => r.data),
  // Valider une réponse d'énigme côté serveur (SEC-003)
  validerEnigme: (code, payload) =>
    publicApi
      .post(`/sessions/by-code/${code}/valider-enigme`, payload)
      .then((r) => r.data),
};

export default api;
